import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Upload, Linkedin, Sparkles, RotateCw, Lock, Trophy, Target, Lightbulb,
  Briefcase, ChevronRight, Zap, Star, TrendingUp, Copy, Send, ArrowRight,
} from "lucide-react";
import Footer from "@/components/Footer";

const QUICK_PROMPTS = [
  "Make it more concise",
  "Add more numbers / metrics",
  "Sound more senior",
  "More approachable / warm",
  "Punchier hook",
  "Match the target role better",
];

const SECTIONS = {
  headline: { label: "Headline", color: "from-blue-500 to-indigo-600", limit: 220 },
  about: { label: "About / Summary", color: "from-fuchsia-500 to-pink-600", limit: 2000 },
  experience_bullet: { label: "Experience Bullet", color: "from-emerald-500 to-teal-600", limit: 220 },
  recommendation_request: { label: "Recommendation Request", color: "from-amber-500 to-orange-600", limit: 300 },
};

function Score({ value, label, testId }) {
  const color = value >= 80 ? "text-emerald-600" : value >= 60 ? "text-amber-600" : "text-rose-600";
  const bar = value >= 80 ? "bg-emerald-500" : value >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div data-testid={testId} className="bg-white border border-zinc-200 p-4">
      <div className={`font-display font-extrabold text-4xl ${color}`}>{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
      <div className="h-1 bg-zinc-100 mt-2"><div className={`h-full ${bar}`} style={{ width: `${value}%` }} /></div>
    </div>
  );
}

function RewritePanel({ sectionKey, currentText, targetRole, isPro }) {
  const cfg = SECTIONS[sectionKey];
  const [text, setText] = useState(currentText || "");
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (extraPrompt) => {
    if (!isPro) {
      toast.error("Rewrites are Premium — upgrade to unlock.");
      return;
    }
    if (text.trim().length < 5) {
      toast.error("Add some current text to rewrite.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/linkedin/rewrite", {
        section: sectionKey,
        current_text: text,
        target_role: targetRole || undefined,
        improvement_prompt: (extraPrompt || prompt || "").trim() || undefined,
      });
      setResult(data);
      setPrompt("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rewrite failed.");
    } finally {
      setBusy(false);
    }
  };

  const applyPreset = (p) => { setPrompt(p); submit(p); };

  return (
    <div data-testid={`rewrite-panel-${sectionKey}`} className="bg-white border border-zinc-200 overflow-hidden">
      <div className={`bg-gradient-to-r ${cfg.color} px-5 py-3 flex items-center gap-2`}>
        <Sparkles className="w-4 h-4 text-white" />
        <span className="font-mono uppercase tracking-wider text-[11px] text-white">Rewrite · {cfg.label}</span>
        <span className="ml-auto text-[10px] font-mono text-white/70">Max {cfg.limit} chars</span>
      </div>
      <div className="p-5 space-y-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={sectionKey === "about" ? 6 : 3}
          placeholder="Paste your current text here…"
          data-testid={`rewrite-input-${sectionKey}`}
          className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900"
        />
        <div className="flex flex-wrap gap-1.5">
          {QUICK_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              disabled={busy || !isPro}
              className="px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors disabled:opacity-40"
              data-testid={`quick-prompt-${sectionKey}-${p.slice(0,4).toLowerCase()}`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder='Or write your own instruction (e.g. "add impact numbers")'
            disabled={!isPro}
            data-testid={`rewrite-prompt-${sectionKey}`}
            className="flex-1 px-3 py-2 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900"
          />
          <button
            onClick={() => submit()}
            disabled={busy || !isPro || text.trim().length < 5}
            data-testid={`rewrite-submit-${sectionKey}`}
            className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium disabled:opacity-40 hover:opacity-90 transition-opacity bg-gradient-to-r ${cfg.color}`}
          >
            {busy ? <RotateCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Rewriting…" : "Rewrite"}
          </button>
        </div>
        {!isPro && (
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 bg-amber-50 border border-amber-200 p-3">
            <Lock className="w-3.5 h-3.5 text-amber-600" />
            <Link to="/pricing" className="text-[#DC1C22] font-medium hover:underline underline-offset-4">Upgrade to Premium</Link>
            <span>to unlock rewrites with iterative prompts.</span>
          </div>
        )}
        {result?.rewrite && (
          <div className="border-t border-zinc-100 pt-4 space-y-2" data-testid={`rewrite-result-${sectionKey}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono uppercase tracking-wider text-[10px] text-zinc-500">Result · {result.char_count} chars</span>
              <button
                onClick={() => { navigator.clipboard.writeText(result.rewrite); toast.success("Copied!"); }}
                className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 border-2 border-dashed border-zinc-300 p-4 text-sm text-zinc-900 whitespace-pre-wrap">
              {result.rewrite}
            </div>
            {(result.changes || []).length > 0 && (
              <ul className="space-y-1 text-xs text-zinc-600 mt-2">
                {result.changes.map((c, i) => (
                  <li key={i} className="flex gap-2"><span className="text-emerald-600">✓</span>{c}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function JobsPanel({ targetRole, isPro }) {
  const [query, setQuery] = useState(targetRole || "");
  const [location, setLocation] = useState("");
  const [country, setCountry] = useState("us");
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState(null);

  useEffect(() => { setQuery(targetRole || ""); }, [targetRole]);

  const search = async () => {
    if (!isPro) { toast.error("Upgrade to Premium to unlock job matches."); return; }
    if (!query.trim()) { toast.error("Enter a role or keyword."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/linkedin/jobs", { query, location: location || undefined, country, results: 10 });
      setJobs(data);
      if (!data.configured) toast.info("Job matching will unlock once Adzuna keys are configured.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Job search failed.");
    } finally { setBusy(false); }
  };

  return (
    <section id="jobs" data-testid="jobs-panel" className="bg-white border border-zinc-200 overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-5 py-3 flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-white" />
        <span className="font-mono uppercase tracking-wider text-[11px] text-white">Premium · Matched Job Offers</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Role/keywords" data-testid="jobs-query" className="col-span-2 px-3 py-2 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900" />
          <input value={location} onChange={(e)=>setLocation(e.target.value)} placeholder="Location (city)" className="px-3 py-2 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900" />
          <select value={country} onChange={(e)=>setCountry(e.target.value)} className="px-3 py-2 border border-zinc-200 text-sm bg-white focus:outline-none focus:border-zinc-900">
            {["us","gb","de","fr","in","za","au","ca","nl","es","it","ch","br","mx"].map((c)=>(<option key={c} value={c}>{c.toUpperCase()}</option>))}
          </select>
        </div>
        <button onClick={search} disabled={busy || !isPro} data-testid="jobs-search" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-medium disabled:opacity-40 hover:opacity-90 transition-opacity">
          {busy ? <RotateCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {busy ? "Searching…" : "Find matching jobs"}
        </button>
        {!isPro && (
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 bg-amber-50 border border-amber-200 p-3">
            <Lock className="w-3.5 h-3.5 text-amber-600" />
            <Link to="/pricing" className="text-[#DC1C22] font-medium hover:underline underline-offset-4">Upgrade to Premium</Link>
            <span>to unlock real-time matched jobs (Adzuna-powered).</span>
          </div>
        )}
        {jobs && !jobs.configured && (
          <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-3">
            ⚠️ Adzuna API not yet configured. Once your team adds ADZUNA_APP_ID + ADZUNA_APP_KEY, live jobs will appear here.
          </div>
        )}
        {jobs?.items?.length > 0 && (
          <ul className="divide-y divide-zinc-100 border-t border-zinc-100" data-testid="jobs-list">
            {jobs.items.map((j)=>(
              <li key={j.id} className="py-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <a href={j.redirect_url} target="_blank" rel="noopener noreferrer" className="font-display font-semibold text-zinc-900 hover:text-[#DC1C22]">{j.title}</a>
                    <div className="text-xs text-zinc-500 mt-0.5">{j.company} · {j.location}</div>
                  </div>
                  {(j.salary_min || j.salary_max) && (
                    <span className="text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-1 border border-emerald-200 shrink-0">
                      {j.salary_currency || ""} {j.salary_min?.toLocaleString?.() || ""}{j.salary_max ? `–${j.salary_max.toLocaleString()}` : ""}
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-600 mt-2 line-clamp-2">{j.description}</p>
                <a href={j.redirect_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#DC1C22] mt-2 hover:underline underline-offset-4">
                  View listing <ArrowRight className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default function LinkedInOptimizer() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const isPro = !!user?.is_pro;

  const analyze = async () => {
    if (!file) { toast.error("Please upload your LinkedIn PDF first."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (role) form.append("target_role", role);
      const { data } = await api.post("/linkedin/analyze", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      toast.success("Profile analyzed — scroll down for the report.");
      setTimeout(() => window.scrollTo({ top: window.innerHeight * 0.7, behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed.");
    } finally { setBusy(false); }
  };

  const s = analysis?.analysis?.sections || {};

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid="linkedin-optimizer-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">

        {/* HERO — colorful */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-8 relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-fuchsia-600 text-white p-8 lg:p-10">
            <div className="absolute -right-8 -top-8 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
            <div className="absolute -right-16 -bottom-16 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="inline-flex items-center justify-center w-7 h-7 bg-white text-blue-700">
                  <Linkedin className="w-4 h-4" strokeWidth={2.5} />
                </span>
                <span className="font-mono uppercase tracking-wider text-[11px] text-blue-100">LinkedIn Optimizer</span>
              </div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Turn your LinkedIn<br />
                into a <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">recruiter magnet.</span>
              </h1>
              <p className="mt-5 text-blue-100 max-w-xl">
                Upload your LinkedIn PDF, get a full profile score with section-by-section fixes, then rewrite Headline, About & Experience with iterative AI prompts.
              </p>
            </div>
          </div>
          <div className="lg:col-span-4 bg-white border border-zinc-200 p-6 lg:p-7">
            <h3 className="font-display font-bold text-lg text-zinc-900 mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" /> How to get your PDF
            </h3>
            <ol className="space-y-2 text-sm text-zinc-700">
              <li className="flex gap-2"><span className="text-blue-600 font-mono">1.</span> Go to your LinkedIn profile</li>
              <li className="flex gap-2"><span className="text-blue-600 font-mono">2.</span> Click <span className="font-mono px-1 py-0.5 bg-zinc-100 text-xs">More</span> → <span className="font-mono px-1 py-0.5 bg-zinc-100 text-xs">Save to PDF</span></li>
              <li className="flex gap-2"><span className="text-blue-600 font-mono">3.</span> Upload it here 👇</li>
            </ol>
          </div>
        </div>

        {/* UPLOAD */}
        <div className="bg-white border border-zinc-200 p-6 lg:p-8 mb-8" data-testid="linkedin-upload-card">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-6">
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1.5">LinkedIn PDF</label>
              <label htmlFor="li-pdf" className="flex items-center gap-3 border-2 border-dashed border-zinc-300 hover:border-blue-500 p-4 cursor-pointer bg-gradient-to-br from-blue-50 to-fuchsia-50 transition-colors">
                <Upload className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-zinc-700 flex-1">{file ? file.name : "Drop or click to upload your LinkedIn PDF"}</span>
                <span className="text-xs font-mono text-zinc-500">.pdf</span>
              </label>
              <input id="li-pdf" ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" data-testid="linkedin-file-input" onChange={(e)=>setFile(e.target.files?.[0] || null)} />
            </div>
            <div className="lg:col-span-4">
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1.5">Target Role <span className="text-zinc-400">(optional)</span></label>
              <input value={role} onChange={(e)=>setRole(e.target.value)} placeholder="e.g. Senior Product Manager" data-testid="linkedin-target-role" className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-blue-500" />
            </div>
            <div className="lg:col-span-2 flex items-end">
              <button onClick={analyze} disabled={busy || !file} data-testid="linkedin-analyze-btn" className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-fuchsia-600 text-white text-sm font-medium disabled:opacity-40 hover:opacity-90 transition-opacity">
                {busy ? <RotateCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? "Analyzing…" : "Analyze"}
              </button>
            </div>
          </div>
        </div>

        {/* ANALYSIS */}
        {analysis && (
          <div className="space-y-6" data-testid="linkedin-analysis-section">
            {/* Overall score card */}
            <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-fuchsia-600 text-white p-6 lg:p-8 relative overflow-hidden">
              <div className="absolute -right-12 -top-12 w-56 h-56 bg-yellow-300/20 rounded-full blur-3xl" />
              <div className="relative flex items-center gap-6 flex-wrap">
                <Trophy className="w-14 h-14 text-yellow-300" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-blue-100">Profile Score</div>
                  <div className="font-display font-extrabold text-6xl leading-none" data-testid="linkedin-overall-score">{analysis.analysis.overall}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-xs font-mono uppercase tracking-widest text-blue-100">Analyzed for</div>
                  <div className="font-display font-bold text-lg">{analysis.profile.name || "your profile"}</div>
                  {role && <div className="text-xs text-blue-100 mt-0.5">Target: {role}</div>}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-6 relative">
                {["headline","about","experience","skills","featured","keywords"].map((k)=>(
                  <div key={k} className="bg-white/10 backdrop-blur border border-white/20 p-3">
                    <div className="text-xs font-mono uppercase tracking-wider text-blue-100">{k}</div>
                    <div className="font-display font-extrabold text-2xl">{s[k]?.score ?? 0}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick wins */}
            {(analysis.analysis.quick_wins || []).length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-l-4 border-amber-500 p-5 lg:p-6" data-testid="linkedin-quick-wins">
                <h3 className="font-display font-bold text-lg text-amber-900 mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-600" /> Quick Wins — biggest ROI first
                </h3>
                <ol className="space-y-2">
                  {analysis.analysis.quick_wins.map((w,i)=>(
                    <li key={i} className="flex gap-3 text-sm text-amber-900"><span className="font-mono text-amber-500 shrink-0">{i+1}.</span>{w}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Section drilldown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {["headline","about","experience","skills"].map((k)=>{
                const sec = s[k] || {};
                return (
                  <div key={k} className="bg-white border border-zinc-200 p-5" data-testid={`section-card-${k}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-display font-bold text-lg text-zinc-900 uppercase tracking-wide">{k}</h3>
                      <span className={`px-3 py-1 font-mono text-sm ${sec.score >= 80 ? "bg-emerald-100 text-emerald-800" : sec.score >= 60 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{sec.score ?? 0}/100</span>
                    </div>
                    {sec.top_fix && (<p className="text-sm text-zinc-700 bg-blue-50 border-l-2 border-blue-500 pl-3 py-2 mb-3"><span className="font-mono text-[10px] uppercase tracking-wider text-blue-700 block">Top fix</span>{sec.top_fix}</p>)}
                    {(sec.strengths || []).length > 0 && (
                      <div className="mb-2">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-700 mb-1">Strengths</div>
                        <ul className="space-y-1 text-xs text-zinc-700">{sec.strengths.map((x,i)=><li key={i} className="flex gap-1.5"><span className="text-emerald-600">✓</span>{x}</li>)}</ul>
                      </div>
                    )}
                    {(sec.issues || []).length > 0 && (
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-rose-700 mb-1">Issues</div>
                        <ul className="space-y-1 text-xs text-zinc-700">{sec.issues.map((x,i)=><li key={i} className="flex gap-1.5"><span className="text-rose-600">✗</span>{x}</li>)}</ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Keywords */}
            {s.keywords && (
              <div className="bg-white border border-zinc-200 p-5 lg:p-6" data-testid="section-keywords">
                <h3 className="font-display font-bold text-lg text-zinc-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-fuchsia-600" /> Keyword Optimization
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-emerald-700 mb-2">Present ({(s.keywords.present_keywords || []).length})</div>
                    <div className="flex flex-wrap gap-1.5">{(s.keywords.present_keywords || []).map((k,i)=><span key={i} className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 border border-emerald-200">{k}</span>)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-rose-700 mb-2">Missing ({(s.keywords.missing_keywords || []).length})</div>
                    <div className="flex flex-wrap gap-1.5">{(s.keywords.missing_keywords || []).map((k,i)=><span key={i} className="px-2 py-0.5 text-xs bg-rose-100 text-rose-800 border border-rose-200">{k}</span>)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Rewrite panels */}
            <div className="space-y-5">
              <h2 className="font-display font-extrabold text-2xl text-zinc-900 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-fuchsia-600" /> Interactive Rewrites
                {!isPro && <span className="ml-2 text-xs font-mono uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 border border-amber-200">Premium</span>}
              </h2>
              <RewritePanel sectionKey="headline" currentText={analysis.profile.headline} targetRole={role} isPro={isPro} />
              <RewritePanel sectionKey="about" currentText={analysis.profile.about} targetRole={role} isPro={isPro} />
              <RewritePanel sectionKey="experience_bullet" currentText={(analysis.profile.experience?.[0]?.bullets || []).join("\n")} targetRole={role} isPro={isPro} />
            </div>

            {/* Recommendation templates */}
            {(analysis.analysis.recommendation_templates || []).length > 0 && (
              <div className="bg-white border border-zinc-200 p-5 lg:p-6" data-testid="rec-templates">
                <h3 className="font-display font-bold text-lg text-zinc-900 mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" /> Recommendation Request Templates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.analysis.recommendation_templates.map((r,i)=>(
                    <div key={i} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-4">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-2">{r.context}</div>
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap">{r.template}</p>
                      <button onClick={()=>{navigator.clipboard.writeText(r.template); toast.success("Copied!");}} className="mt-2 text-xs text-amber-700 hover:underline underline-offset-4 inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Jobs */}
            <JobsPanel targetRole={role} isPro={isPro} />
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
